import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function WhatsAppWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSeenBadge, setHasSeenBadge] = useState(false);

  const phoneNumber = "916203770581";
  const message = encodeURIComponent("Hi NotesCrafter, I need help with printing my notes. Can you please call/chat with me?");
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;

  const handleOpen = () => {
    setIsOpen(!isOpen);
    setHasSeenBadge(true);
  };

  const handleStartLiveChat = () => {
    setIsOpen(false);
    if (window.initChatWidget) {
      window.initChatWidget({
        widgetToken: "db33d",
        hide_launcher: true,
        show_widget_form: true,
        show_close_button: true,
        launch_widget: true,
        show_send_button: true,
        theme: "system"
      });
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="mb-4 w-72 rounded-2xl bg-[#0B141A] border border-white/10 shadow-2xl overflow-hidden text-white"
          >
            {/* Header */}
            <div className="bg-[#075E54] p-4 flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden flex items-center justify-center p-1.5 border border-white/20">
                  <img 
                    src="/logo.v2.png" 
                    alt="NotesCrafter Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#25D366] border-2 border-[#075E54]" />
              </div>
              <div>
                <h4 className="font-bold text-sm">NotesCrafter Support</h4>
                <p className="text-[10px] text-white/80">Active now • Replies instantly</p>
              </div>
            </div>

            {/* Message Body */}
            <div className="p-4 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat bg-opacity-5 min-h-[80px] flex items-center justify-center">
              <div className="bg-[#202C33] rounded-lg p-3 text-xs border border-white/5 max-w-[90%] text-neutral-200">
                Hi there! 👋 Stuck with uploading or printing your notes? Choose a channel below to start chatting:
              </div>
            </div>

            {/* Actions */}
            <div className="p-3 bg-[#111B21] border-t border-white/5 flex flex-col gap-2">
              {/* WhatsApp Option */}
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2 bg-[#25D366] hover:bg-[#20ba5a] text-black font-bold text-xs rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-md"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M12.012 2c-5.506 0-9.989 4.478-9.99 9.984a9.96 9.96 0 001.37 5.05L2 22l5.14-1.348a9.927 9.927 0 004.873 1.28h.005c5.507 0 9.99-4.479 9.992-9.985a9.972 9.972 0 00-2.925-7.064A9.932 9.932 0 0012.012 2zm5.727 14.048c-.246.688-1.428 1.332-1.966 1.41-.492.072-1.127.132-3.268-.744-2.738-1.12-4.503-3.879-4.639-4.062-.136-.18-1.11-1.474-1.11-2.81 0-1.338.702-1.996.953-2.259.25-.262.547-.328.729-.328.182 0 .364.002.525.01.173.007.404-.067.63.483.235.57.802 1.954.87 2.095.068.143.113.31.018.5-.096.19-.144.31-.284.472-.14.163-.294.364-.42.488-.144.14-.294.293-.127.58.167.287.747 1.232 1.6 1.993.737.657 1.357.859 1.66 1.002.302.143.48.12.66-.08.18-.2.78-.907.99-1.22.208-.31.417-.26.702-.153.287.108 1.82.859 2.133 1.014.313.156.52.23.595.36.075.132.075.76-.171 1.448z" />
                </svg>
                <span>Chat on WhatsApp</span>
              </a>

              {/* Live Chat Option */}
              <button
                onClick={handleStartLiveChat}
                className="w-full py-2 bg-white/10 hover:bg-white/15 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2 transition-all duration-200 border border-white/10"
              >
                <svg className="w-4 h-4 fill-none stroke-current" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>Chat Live Here</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Circle Button */}
      <motion.button
        onClick={handleOpen}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        className="w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-xl border border-white/20 relative cursor-pointer"
      >
        <svg className="w-7 h-7 fill-current" viewBox="0 0 24 24">
          <path d="M12.012 2c-5.506 0-9.989 4.478-9.99 9.984a9.96 9.96 0 001.37 5.05L2 22l5.14-1.348a9.927 9.927 0 004.873 1.28h.005c5.507 0 9.99-4.479 9.992-9.985a9.972 9.972 0 00-2.925-7.064A9.932 9.932 0 0012.012 2zm5.727 14.048c-.246.688-1.428 1.332-1.966 1.41-.492.072-1.127.132-3.268-.744-2.738-1.12-4.503-3.879-4.639-4.062-.136-.18-1.11-1.474-1.11-2.81 0-1.338.702-1.996.953-2.259.25-.262.547-.328.729-.328.182 0 .364.002.525.01.173.007.404-.067.63.483.235.57.802 1.954.87 2.095.068.143.113.31.018.5-.096.19-.144.31-.284.472-.14.163-.294.364-.42.488-.144.14-.294.293-.127.58.167.287.747 1.232 1.6 1.993.737.657 1.357.859 1.66 1.002.302.143.48.12.66-.08.18-.2.78-.907.99-1.22.208-.31.417-.26.702-.153.287.108 1.82.859 2.133 1.014.313.156.52.23.595.36.075.132.075.76-.171 1.448z" />
        </svg>

        {/* Pulse Ring */}
        <span className="absolute -inset-0.5 rounded-full border-2 border-[#25D366] animate-ping opacity-25 pointer-events-none" />

        {/* Unread Alert Badge */}
        {!hasSeenBadge && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white font-bold text-[10px] w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border-2 border-black animate-bounce shadow">
            1
          </span>
        )}
      </motion.button>
    </div>
  );
}
